import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, LockKey, Envelope, Warning, ShieldCheck, RocketLaunch, X, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, error } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  function scrollToLogin() {
    const el = document.getElementById("login-identifier");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus(), 400);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const ok = await login(identifier, password);
    setBusy(false);
    if (ok) nav("/");
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-white">
      {/* Left panel — LUROMA neutral welcome */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{
             background: "radial-gradient(1200px 800px at 10% 0%, #172554 0%, #0b1027 55%, #05070f 100%)",
           }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 flex-1">
            <img
              src="/assets/luroma_logo.png"
              alt="LUROMA"
              data-testid="luroma-hero-logo"
              className="h-28 lg:h-32 xl:h-36 w-auto drop-shadow-[0_6px_24px_rgba(0,47,167,0.5)]"
            />
          </div>
          <button
            type="button"
            data-testid="landing-access-luroma-top"
            onClick={scrollToLogin}
            className="hidden lg:flex items-center gap-1.5 bg-white text-[#0b1027] hover:bg-[#FFC000] px-4 py-2 text-sm font-bold transition-colors"
          >
            <LockKey size={14} weight="bold" /> Access LUROMA
          </button>
        </div>

        <div className="max-w-lg">
          <div className="phizo-label text-[10px] text-[#FFC000] mb-3">OPERATIONS PLATFORM</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl xl:text-5xl leading-[1.05]">
            Empower your business.<br />
            <span className="text-[#FFC000]">Master your data.</span>
          </h1>
          <p className="text-sm text-white/70 mt-6 leading-relaxed">
            LUROMA delivers white-label operations control rooms for quarries, mines
            and industrial operators across Southern Africa — real-time stock,
            production, HR & payroll, purchasing and fleet, all in one place.
          </p>
          <TrustedByStrip />
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="landing-get-started-btn"
              onClick={() => setLeadOpen(true)}
              className="flex items-center gap-2 bg-[#FFC000] hover:bg-white text-[#0b1027] px-5 py-2.5 font-bold text-sm transition-colors"
            >
              <RocketLaunch size={16} weight="bold" /> Get Started
            </button>
            <button
              type="button"
              data-testid="landing-access-luroma-cta"
              onClick={scrollToLogin}
              className="flex items-center gap-2 bg-white text-[#0b1027] hover:bg-[#FFC000] px-5 py-2.5 font-bold text-sm transition-colors"
            >
              <LockKey size={16} weight="bold" /> Access LUROMA
            </button>
            <a
              href="mailto:hello@luroma.co.zm?subject=Contact"
              data-testid="landing-contact-link"
              className="flex items-center gap-2 border border-white/30 hover:border-white text-white px-5 py-2.5 font-medium text-sm transition-colors"
            >
              Contact us
            </a>
          </div>
          <div className="mt-6 flex items-center gap-3 text-xs text-white/60">
            <ShieldCheck size={14} weight="bold" className="text-[#FFC000]" />
            <span>Strict tenant isolation · Encrypted at rest · SOC-ready audit trail</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <a href="tel:+260962500076" className="font-semibold text-[#FFC000] hover:text-white transition-colors">
            ☎ +260 962 500 076
          </a>
          <a href="mailto:hello@luroma.co.zm?subject=I%27d%20like%20a%20control%20room"
             className="font-semibold text-white/80 hover:text-[#FFC000] transition-colors">
            ✉ hello@luroma.co.zm
          </a>
          <span className="phizo-label text-white/40 ml-auto">v1.0 · Kitwe, Zambia</span>
        </div>
      </div>

      {/* Right panel — sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[#fafafa]">
        <div className="w-full max-w-md bg-white border border-[#e4e4e7] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-[#e4e4e7]">
            <img
              src="/assets/luroma_logo.png"
              alt="LUROMA"
              className="h-11 w-auto"
            />
            <div>
              <div className="font-heading text-lg font-black tracking-tight leading-none">
                LUROMA Control Room
              </div>
              <div className="phizo-label text-[10px] text-[#71717a] mt-1">
                Operator sign-in
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="phizo-label mb-2">Sign In</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">
              Welcome back.
            </h2>
            <p className="text-sm text-[#52525b] mt-2">
              Access your organisation's operations dashboard.
            </p>
          </div>

          {error ? (
            <div
              data-testid="login-error"
              className="mb-4 flex items-start gap-2 border border-[#FF2A2A] bg-[#FFF2F2] p-3 text-sm text-[#FF2A2A]"
            >
              <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="phizo-label block mb-2">Username or Email</label>
              <div className="relative">
                <Envelope size={18} className="absolute left-3 top-3 text-[#a1a1aa]" />
                <input
                  id="login-identifier"
                  data-testid="login-email-input"
                  type="text"
                  required
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="w-full border border-[#e4e4e7] bg-white pl-10 pr-3 py-2.5 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                />
              </div>
            </div>
            <div>
              <label className="phizo-label block mb-2">Password</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-3 top-3 text-[#a1a1aa]" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[#e4e4e7] bg-white pl-10 pr-10 py-2.5 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                />
                <button
                  type="button"
                  data-testid="password-peek-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-2 h-7 w-7 flex items-center justify-center text-[#71717a] hover:text-[#002FA7]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#001f7a] text-white py-2.5 font-medium disabled:opacity-60"
            >
              {busy ? "Signing in…" : <>Enter Control Room <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>

          <div className="mt-8 text-[10px] phizo-label text-[#a1a1aa] text-center">
            Powered by LUROMA · Trouble signing in? Contact your organisation admin.
          </div>
          <div className="mt-4 text-center lg:hidden">
            <button
              type="button"
              data-testid="landing-get-started-btn-mobile"
              onClick={() => setLeadOpen(true)}
              className="text-xs font-semibold text-[#002FA7] underline"
            >
              Not a client yet? Get started →
            </button>
          </div>
        </div>
      </div>

      {leadOpen && <LeadModal onClose={() => setLeadOpen(false)} />}
    </div>
  );
}

function LeadModal({ onClose }) {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "", industry: "", description: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Company, name and email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="lead-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-[#71717a] hover:text-black p-1"
          data-testid="lead-modal-close"
        >
          <X size={18} weight="bold" />
        </button>

        {done ? (
          <div className="p-10 text-center space-y-4" data-testid="lead-thanks">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#10B981] flex items-center justify-center text-white">
              <CheckCircle size={40} weight="fill" />
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tighter">Thanks, {form.contact_name.split(" ")[0]}.</h2>
            <p className="text-sm text-[#52525b] max-w-sm mx-auto">
              A LUROMA rep will reach out within one business day to schedule your kickoff call
              and walk through your control room.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#001f7a]"
              data-testid="lead-thanks-close"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 space-y-5">
            <div className="flex items-center gap-3 pb-4 border-b border-[#e4e4e7]">
              <img src="/assets/luroma_logo.png" alt="LUROMA" className="h-10 w-auto" />
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Get Started with LUROMA</div>
                <h2 className="font-heading text-xl font-black tracking-tighter">Tell us about your business.</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LeadField label="Company name" required>
                <input
                  data-testid="lead-company"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="e.g. Copperfields Ltd"
                />
              </LeadField>
              <LeadField label="Your name" required>
                <input
                  data-testid="lead-contact-name"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="Jane Musonda"
                />
              </LeadField>
              <LeadField label="Email" required>
                <input
                  data-testid="lead-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="you@company.co.zm"
                />
              </LeadField>
              <LeadField label="Phone">
                <input
                  data-testid="lead-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="+260 977 …"
                />
              </LeadField>
              <LeadField label="Industry / business type" span={2}>
                <select
                  data-testid="lead-industry"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                >
                  <option value="">Select an industry…</option>
                  <option>Quarry / aggregates</option>
                  <option>Mining</option>
                  <option>Construction</option>
                  <option>Manufacturing</option>
                  <option>Logistics / fleet</option>
                  <option>Agriculture</option>
                  <option>Other</option>
                </select>
              </LeadField>
              <LeadField label="What do you need?" span={2}>
                <textarea
                  data-testid="lead-description"
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
                  placeholder="Briefly describe what you'd like to run through LUROMA — stock, HR, production, fleet, and roughly how many people or sites."
                />
              </LeadField>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-[#e4e4e7]">
              <div className="text-[10px] text-[#a1a1aa] max-w-[250px]">
                We'll only use these details to reach out about your LUROMA setup.
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="lead-submit"
                className="bg-[#002FA7] hover:bg-[#001f7a] text-white font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2"
              >
                {busy ? "Sending…" : <>Submit <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function LeadField({ label, required, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "sm:col-span-2" : ""}`}>
      <div className="phizo-label text-[10px] mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

function TrustedByStrip() {
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/tenants/public");
        if (!cancelled) setTenants(Array.isArray(data) ? data : []);
      } catch { /* silent — strip is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!tenants.length) return null;
  return (
    <div className="mt-8" data-testid="trusted-by-strip">
      <div className="phizo-label text-[10px] text-white/50 mb-3">
        Trusted by operators across Zambia
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {tenants.map((t) => (
          <div
            key={t.code}
            className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-1.5"
            data-testid={`trusted-by-${t.code}`}
            title={t.name}
          >
            {t.logo_url ? (
              <img
                src={t.logo_url}
                alt={t.name}
                className="h-6 w-6 object-contain bg-white rounded-sm"
              />
            ) : (
              <div
                className="h-6 w-6 flex items-center justify-center text-white text-[10px] font-black rounded-sm"
                style={{ background: t.brand_primary || "#002FA7" }}
              >
                {t.code?.[0] || "?"}
              </div>
            )}
            <span className="text-xs font-semibold text-white whitespace-nowrap">
              {t.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
